<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>Add Vocus NBN Server</DialogTitle>
            </DialogHeader>
            <ScrollArea class="h-[500px] rounded-md p-3">
                <div class="space-y-3">
                    <FormField name="title" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Title (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Title" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="account_username" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Account Username (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Account Username" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="account_password" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Account Password (Required)</FormLabel>
                            <FormControl>
                                <Password
                                    v-bind="componentField"
                                    placeholder="Account Password"
                                    can-generate
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="auth_client_id" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Auth Client ID (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Auth Client ID" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="auth_client_secret" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Auth Client Secret (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Auth Client Secret" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="api_client_id" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>API Client ID (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="API Client ID" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="api_client_secret" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>API Client Secret (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="API Client Secret" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="wsm_cert_pass" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>WSM Certificate Password (Required)</FormLabel>
                            <FormControl>
                                <Password
                                    v-bind="componentField"
                                    placeholder="WSM Certificate Password"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="wsm_access_key" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>WSM Access Key (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="WSM Access Key" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField v-slot="{ value, handleChange }" name="wsm_cert_file">
                        <FormItem>
                            <FormLabel>WSM Certificate (Required)</FormLabel>
                            <FormControl>
                                <File
                                    type="file"
                                    :model-value="value"
                                    @update:model-value="handleChange"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="proxy_host" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Proxy Host (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Proxy Host" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="proxy_port" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Proxy Port (Required)</FormLabel>
                            <FormControl>
                                <Input
                                    type="number"
                                    v-bind="componentField"
                                    placeholder="Proxy Port"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
            </ScrollArea>
            <DialogFooter>
                <Button type="button" variant="secondary" :disabled="isCreatePending" @click="close"
                    >Cancel</Button
                >
                <Button
                    type="button"
                    :loading="isCreatePending"
                    :disabled="!meta.valid || !meta.dirty"
                    @click="submit"
                    >Save</Button
                >
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import File from '@/components/ui/input/File.vue'
import Password from '@/components/ui/input/Password.vue'
import ScrollArea from '@/components/ui/scroll-area/ScrollArea.vue'
import { smsGlobalServerFormSchema } from '@/lib/schema/v1/sms-global'
import { setFieldsError } from '@/lib/utils'
import { useAddVocusNBNServerMutation } from '@/queries/vocus-nbn'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { computed, isProxy, toRaw } from 'vue'
import { z } from 'zod'

const open = defineModel()

const { mutate: addServer, isPending: isCreatePending } = useAddVocusNBNServerMutation()

const schema = z.object({
    title: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    account_username: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    account_password: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    auth_client_id: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    auth_client_secret: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    api_client_id: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    api_client_secret: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    wsm_cert_pass: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    wsm_access_key: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    wsm_cert_file: z.any(),
    proxy_host: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    proxy_port: z.number().min(1, {
        message: "You can't leave this empty.",
    }),
})

const form = useForm({
    validationSchema: toTypedSchema(schema),
})

const meta = computed(() => form.meta.value)

const submit = form.handleSubmit(async (values) => {
    if (!meta.value.dirty || !meta.value.valid) return

    let wsmCert = values.wsm_cert_file

    if (isProxy(wsmCert)) {
        wsmCert = toRaw(wsmCert)
    }

    const payload = {
        ...values,
        wsm_cert: wsmCert[0],
    }

    addServer(payload, {
        onSuccess: () => {
            close()
        },
        onError: (error) => {
            console.log(error)
            setFieldsError(error, form.setFieldError)
        },
    })
})

const close = () => {
    open.value = false
    form.resetForm()
}
</script>
